// Utilidades compartidas del módulo Contabilidad. Habla a PostgREST (esquema contabilidad).
import { apiClient } from '@/lib/api/client';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

export const CONT = { headers: { 'Content-Profile': 'contabilidad', 'Accept-Profile': 'contabilidad' } };
export const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
export const field = 'flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
export const usd = (v: number | string | null | undefined) =>
  Number(v ?? 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
export const hoy = fechaLocal();

// ----------------------------------------------- ESCALA DE PRESENTACIÓN ------
// Un reporte gerencial en millones se lee de un vistazo; en dólares con centavos,
// no. Vive aquí para que el Dashboard y los reportes de Inteligencia de negocios
// usen exactamente el mismo criterio y las mismas etiquetas.
export const ESCALAS: { v: number; label: string; nota: string }[] = [
  { v: 1, label: 'Normal', nota: '' },
  { v: 1000, label: 'Miles', nota: 'Cifras en MILES de USD' },
  { v: 1000000, label: 'Millones', nota: 'Cifras en MILLONES de USD' },
];

export const numFmt = (v: number) =>
  v.toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Importe con la escala aplicada. Al escalar se QUITA el símbolo de moneda a
 *  propósito: mostrar "$ 175,45" cuando en realidad son 175.450 induce a error;
 *  la unidad la dice la etiqueta del tablero o el subtítulo de la exportación. */
export const usdEsc = (v: number | string | null | undefined, escala = 1) =>
  escala === 1 ? usd(v) : numFmt(Number(v ?? 0) / escala);

export const notaDeEscala = (escala: number) => ESCALAS.find((e) => e.v === escala)?.nota ?? '';
export const inicioMes = hoy.slice(0, 8) + '01';

export interface Cuenta {
  id: string; codigo: string; nombre: string; tipo: string; naturaleza: string;
  es_movimiento: boolean; parent_id: string | null; es_activo: boolean;
  categoria_niif18?: string | null;
  naturaleza_gasto?: string | null;
}
export interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }

export const rpc = async <T,>(fn: string, params: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post(`/rpc/${fn}`, params, CONT)).data as T;

export const cargarPlan = () => rpc<Cuenta[]>('cont_plan');
export const partnersList = async (): Promise<PartnerOpt[]> =>
  (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [];

// Dimensiones analíticas: ahora catálogos propios del esquema contabilidad
export interface DimOpt { id: string; codigo: string; nombre: string; estado?: string }
export const centrosList = () => rpc<DimOpt[]>('cont_centros');
export const proyectosList = () => rpc<DimOpt[]>('cont_proyectos');

// CRUD directo a /rest/contabilidad.<tabla> (RLS por empresa)
const repr = { headers: { ...CONT.headers, Prefer: 'return=representation' } };
export const restCont = (t: string) => ({
  list: async <T,>(query = ''): Promise<T[]> => (await apiClient.get<T[]>(`/rest/contabilidad.${t}${query}`, CONT)).data,
  create: async (body: unknown) => (await apiClient.post(`/rest/contabilidad.${t}`, body, repr)).data,
  update: async (id: string, body: unknown) => (await apiClient.patch(`/rest/contabilidad.${t}?id=eq.${id}`, body, repr)).data,
  remove: async (id: string) => (await apiClient.patch(`/rest/contabilidad.${t}?id=eq.${id}`, { deleted_at: new Date().toISOString() }, repr)).data,
});

// Módulo generador → etiqueta e color para badges en la lista de asientos
export const MODULO_LABEL: Record<string, string> = {
  manual: 'Manual', financiero: 'Financiero', nomina: 'Nómina', pos: 'POS',
  control_bancario: 'Control Bancario', inventario: 'Inventario', cierre: 'Cierre', apertura: 'Apertura', otro: 'Otro',
};

// Exporta a CSV con protección anti-inyección de fórmulas (=,+,-,@) + BOM UTF-8.
export function exportarCSV(nombre: string, headers: string[], filas: (string | number | null)[][]) {
  const esc = (v: string | number | null) => {
    let s = v == null ? '' : String(v);
    if (/^[=+\-@]/.test(s)) s = "'" + s;
    if (/[",\n;]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const csv = [headers, ...filas].map((r) => r.map(esc).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url; a.download = `${nombre}.csv`; a.click();
  URL.revokeObjectURL(url);
}
